import { useEffect, useState } from "react";
import { Check, CheckSquare, Package, Layers, Truck } from "react-feather";
import Step1SelectItems from "./Step1SelectItems";
import Step2PackBoxes from "./Step2PackBoxes";
import Step3BundlePackages from "./Step3BundlePackages";
import Step4CarrierDispatch from "./Step4CarrierDispatch";

const ShippingStepper = ({
  unassignedAlibabaOrders = [],
  selectedUnassigned = [],
  setSelectedUnassigned,
  handleToggleUnassigned,
  handleSelectAllUnassigned,
  handleMarkArrived,
  handleCreateShippingOrder,
  creatingShipping = false,
  renderOrderItems,
  searchTerm = "",
  wegeOrderId,
  loosePackages = [],
  selectedLoosePackages = [],
  setSelectedLoosePackages,
  handleCreateShippingFromPackages,
  creatingShippingFromPackages = false,
  groups = [],
  activeShippingOrderGroup,
  setActiveShippingOrderGroup,
  providerSelections = {},
  handleProviderSelect,
  onPlaceOrder,
  onPrint,
  printLoading = {},
  handleRecalculateRate,
  rateLoading = {},
  handleFetchAramexTracking,
  trackingLoading = {},
  trackingData = {},
  onRefresh,
  currentStepProp,
  setCurrentStepProp,
  t,
}) => {
  const getInitialStep = () => {
    if (activeShippingOrderGroup) return 3;
    if (loosePackages.length > 0 && unassignedAlibabaOrders.length === 0) return 2;
    return 0;
  };

  const [internalStep, setInternalStep] = useState(getInitialStep);
  const currentStep =
    currentStepProp !== null && currentStepProp !== undefined
      ? currentStepProp
      : internalStep;

  const setCurrentStep = (step) => {
    setInternalStep(step);
    if (setCurrentStepProp) {
      setCurrentStepProp(step);
    }
  };

  useEffect(() => {
    if (activeShippingOrderGroup) {
      setCurrentStep(3);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeShippingOrderGroup]);

  const arrivedOrders = unassignedAlibabaOrders.filter(
    (o) => o.status?.toLowerCase() === "arrived",
  );

  const selectedArrivedOrderIds = selectedUnassigned.filter((id) => {
    const o = unassignedAlibabaOrders.find((ord) => ord.id === id);
    return o?.status?.toLowerCase() === "arrived";
  });

  const activePackingOrderIds =
    selectedArrivedOrderIds.length > 0
      ? selectedArrivedOrderIds
      : arrivedOrders.map((o) => o.id);

  const stepConfig = [
    {
      index: 0,
      title: t("stepper.step1Title", "1. Select Items"),
      subtitle: t("stepper.step1Subtitle", "Select 1688 orders"),
      icon: CheckSquare,
      badge:
        selectedUnassigned.length > 0
          ? `${selectedUnassigned.length} ${t("stepper.ordersSelected", "orders selected")}`
          : unassignedAlibabaOrders.length > 0
            ? `${unassignedAlibabaOrders.length} ${t("table.order.total", "orders")}`
            : null,
      badgeColor: "#3b82f6",
      isClickable: true,
    },
    {
      index: 1,
      title: t("stepper.step2Title", "2. Pack Boxes"),
      subtitle: t("stepper.step2Subtitle", "Weigh & pack boxes"),
      icon: Package,
      badge:
        loosePackages.length > 0
          ? `${loosePackages.length} ${t("stepper.boxesReady", "box(es) recorded")}`
          : null,
      badgeColor: "#10b981",
      isClickable: activePackingOrderIds.length > 0 || loosePackages.length > 0,
    },
    {
      index: 2,
      title: t("stepper.step3Title", "3. Bundle Shipment"),
      subtitle: t("stepper.step3Subtitle", "Consolidate shipment"),
      icon: Layers,
      badge:
        selectedLoosePackages.length > 0
          ? `${selectedLoosePackages.length} ${t("stepper.boxesSelected", "boxes selected")}`
          : loosePackages.length > 0
            ? `${loosePackages.length} ${t("stepper.boxesReady", "boxes")}`
            : null,
      badgeColor: "#8b5cf6",
      isClickable: loosePackages.length > 0,
    },
    {
      index: 3,
      title: t("stepper.step4Title", "4. Dispatch & Print"),
      subtitle: t("stepper.step4Subtitle", "Dispatch & print waybill"),
      icon: Truck,
      badge: activeShippingOrderGroup
        ? `#${activeShippingOrderGroup.shipping_order?.id || ""}`
        : null,
      badgeColor: "#f59e0b",
      isClickable: !!activeShippingOrderGroup || groups.length > 0,
    },
  ];

  const progressPercent = Math.round(((currentStep + 1) / 4) * 100);

  return (
    <div
      style={{
        backgroundColor: "#ffffff",
        borderRadius: "12px",
        border: "1px solid #e2e8f0",
        boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
        padding: "20px 24px",
      }}
    >
      {/* Modern Stepper Header */}
      <div style={{ marginBottom: "22px", paddingBottom: "18px", borderBottom: "1px solid #f1f5f9" }}>
        {/* Progress Bar Track */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
          <span style={{ fontSize: "13px", fontWeight: 600, color: "#1e293b" }}>
            {stepConfig[currentStep]?.title}
            <span style={{ fontWeight: 400, color: "#64748b", marginLeft: "8px" }}>
              • {stepConfig[currentStep]?.subtitle}
            </span>
          </span>
          <span style={{ fontSize: "12px", fontWeight: 700, color: "#2563eb" }}>
            {progressPercent}% {t("stepper.stepProgress", "Progress")}
          </span>
        </div>
        <div
          style={{
            height: "5px",
            backgroundColor: "#f1f5f9",
            borderRadius: "999px",
            overflow: "hidden",
            marginBottom: "16px",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${progressPercent}%`,
              background: "linear-gradient(90deg, #3b82f6 0%, #6366f1 100%)",
              borderRadius: "999px",
              transition: "width 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          />
        </div>

        {/* 4 Interactive Step Cards */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
            gap: "12px",
          }}
        >
          {stepConfig.map((s) => {
            const isActive = currentStep === s.index;
            const isCompleted = currentStep > s.index;
            const Icon = isCompleted ? Check : s.icon;
            const isClickable = s.isClickable;

            return (
              <div
                key={s.index}
                onClick={() => {
                  if (isClickable) {
                    setCurrentStep(s.index);
                  }
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "12px 14px",
                  borderRadius: "10px",
                  cursor: isClickable ? "pointer" : "not-allowed",
                  opacity: isClickable || isActive ? 1 : 0.6,
                  border: isActive
                    ? "2px solid #2563eb"
                    : isCompleted
                      ? "1px solid #cbd5e1"
                      : "1px solid #e2e8f0",
                  backgroundColor: isActive
                    ? "rgba(37, 99, 235, 0.04)"
                    : isCompleted
                      ? "#f8fafc"
                      : "#ffffff",
                  boxShadow: isActive
                    ? "0 4px 12px rgba(37, 99, 235, 0.12)"
                    : "none",
                  transition: "all 0.2s ease",
                  transform: isActive ? "translateY(-1px)" : "none",
                }}
              >
                {/* Step Icon Badge */}
                <div
                  style={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    backgroundColor: isActive
                      ? "#2563eb"
                      : isCompleted
                        ? "#10b981"
                        : "#f1f5f9",
                    color: isActive || isCompleted ? "#ffffff" : "#64748b",
                    boxShadow: isActive
                      ? "0 2px 8px rgba(37, 99, 235, 0.35)"
                      : isCompleted
                        ? "0 2px 8px rgba(16, 185, 129, 0.25)"
                        : "none",
                  }}
                >
                  <Icon size={18} strokeWidth={isCompleted ? 2.5 : 2} />
                </div>

                {/* Step Titles & Subtitle */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "6px",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "13px",
                        fontWeight: isActive ? 700 : 600,
                        color: isActive ? "#1e293b" : "#334155",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {s.title}
                    </span>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "4px",
                      marginTop: "2px",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "11px",
                        color: isActive ? "#2563eb" : "#64748b",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {s.subtitle}
                    </span>

                    {s.badge && (
                      <span
                        style={{
                          fontSize: "10px",
                          fontWeight: 700,
                          padding: "1px 6px",
                          borderRadius: "999px",
                          backgroundColor: `${s.badgeColor}18`,
                          color: s.badgeColor,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {s.badge}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Step 1: Select Orders */}
      {currentStep === 0 && (
        <Step1SelectItems
          orders={unassignedAlibabaOrders}
          selectedOrderIds={selectedUnassigned}
          onToggleOrder={handleToggleUnassigned}
          onSelectAll={handleSelectAllUnassigned}
          onMarkArrived={handleMarkArrived}
          onProceedToPacking={() => setCurrentStep(1)}
          onDirectCreateShipping={handleCreateShippingOrder}
          creatingShipping={creatingShipping}
          renderOrderItems={renderOrderItems}
          searchTerm={searchTerm}
          t={t}
        />
      )}

      {/* Step 2: Pack Selected Orders */}
      {currentStep === 1 && (
        <Step2PackBoxes
          selectedOrderIds={activePackingOrderIds}
          wegeOrderId={wegeOrderId}
          loosePackagesCount={loosePackages.length}
          onBack={() => setCurrentStep(0)}
          onProceedToBundle={() => setCurrentStep(2)}
          onRefresh={onRefresh}
          t={t}
        />
      )}

      {/* Step 3: Bundle Packages */}
      {currentStep === 2 && (
        <Step3BundlePackages
          loosePackages={loosePackages}
          selectedLoosePackages={selectedLoosePackages}
          setSelectedLoosePackages={setSelectedLoosePackages}
          onCreateShippingFromPackages={async () => {
            const res = await handleCreateShippingFromPackages();
            if (res !== false) {
              setCurrentStep(3);
            }
          }}
          creatingShipping={creatingShippingFromPackages}
          onBack={() => setCurrentStep(1)}
          t={t}
        />
      )}

      {/* Step 4: Dispatch & Print */}
      {currentStep === 3 && (
        <Step4CarrierDispatch
          group={activeShippingOrderGroup}
          providerSelections={providerSelections}
          onProviderSelect={handleProviderSelect}
          onPlaceOrder={onPlaceOrder}
          onPrint={onPrint}
          printLoading={printLoading}
          onRecalculateRate={handleRecalculateRate}
          rateLoading={rateLoading}
          onFetchTracking={handleFetchAramexTracking}
          trackingLoading={trackingLoading}
          trackingData={trackingData}
          onBack={() => setCurrentStep(2)}
          onFulfillMore={() => {
            setActiveShippingOrderGroup(null);
            setCurrentStep(0);
          }}
          hasMoreUnassigned={
            unassignedAlibabaOrders.length > 0 || loosePackages.length > 0
          }
          t={t}
        />
      )}
    </div>
  );
};

export default ShippingStepper;
